import { ICanvasScaler } from "by/gamefactory/canvas/ICanvasScaler";
import { PromiseCompletionSource } from "by/gamefactory/shared/async/PromiseCompletionSource";
import { gsap } from "gsap";
import { IDestroyOptions, Rectangle, Sprite } from "pixi.js";
import { BaseScalableView } from "../../../common/BaseScalableView";
import { ButtonWithScaleAnimation } from "../../../common/components/ButtonWithScaleAnimation";
import { Hand } from "../../../common/components/Hand";
import { CommonResourcesPackage } from "../../../common/resources/CommonResourcesPackage";
import { SoundsResourcePackageWrapper } from "../../../common/resources/SoundsResourcePackageWrapper";
import { FullscreenInputBlocker } from "../../../game/components/FullscreenInputBlocker";
import { IGameController } from "../../../game/controllers/IGameController";
import { PiggyAnimationPromiseHolder } from "../../buildingUpgradeItem/components/PiggyAnimationPromiseHolder";
import { CurrentPiggyState } from "../../piggy/components/piggycontentview/CurrentPiggyContentView";
import { CheckoutPiggyClaimDescriptionComponent } from "../components/CheckoutPiggyClaimDescriptionComponent";
import { CheckoutPiggyHeaderComponent } from "../components/CheckoutPiggyHeaderComponent";
import { CheckoutPiggyPiggyDescriptionComponent } from "../components/CheckoutPiggyPiggyDescriptionComponent";
import { CheckoutPiggyTokenDescriptionComponent } from "../components/CheckoutPiggyTokenDescriptionComponent";
import { CheckoutPiggyResourcePackage } from "../resources/CheckoutPiggyResourcePackage";

export class CheckoutPiggyPopupView extends BaseScalableView<void> {
    private readonly commonResources: CommonResourcesPackage;
    private readonly introResources: CheckoutPiggyResourcePackage;
    private readonly animations: gsap.core.Timeline;
    private readonly gameController: IGameController;
    private readonly soundResources: SoundsResourcePackageWrapper;
    private readonly piggyAnimationPcs: PiggyAnimationPromiseHolder;

    private viewBackground: Sprite;
    private tokenBackground: Sprite;
    private topLight: Sprite;
    private leftBlueLight: Sprite;
    private rightBlueLight: Sprite;
    private bottomLight: Sprite;
    private bigPiggy: Sprite;
    private header: CheckoutPiggyHeaderComponent;
    private tokenDescription: CheckoutPiggyTokenDescriptionComponent;
    private piggyDescription: CheckoutPiggyPiggyDescriptionComponent;
    private claimDescription: CheckoutPiggyClaimDescriptionComponent;
    private piggyButton: ButtonWithScaleAnimation;
    private closeButton: ButtonWithScaleAnimation;
    private inputBlocker: FullscreenInputBlocker;
    private hand: Hand;

    public constructor(
        operationControls: PromiseCompletionSource<void>,
        canvasScaler: ICanvasScaler,
        commonResources: CommonResourcesPackage,
        introResources: CheckoutPiggyResourcePackage,
        gameController: IGameController,
        soundResources: SoundsResourcePackageWrapper,
        piggyAnimationPcs: PiggyAnimationPromiseHolder
    ) {
        super(operationControls, canvasScaler);
        this.commonResources = commonResources;
        this.introResources = introResources;
        this.gameController = gameController;
        this.soundResources = soundResources;
        this.piggyAnimationPcs = piggyAnimationPcs;
        this.animations = new gsap.core.Timeline({ paused: true });
        this.build();
        this.layout(this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
        this.closeButton.onUp.add(this.onCloseButtonClickHandle, this);
        this.piggyButton.onUp.add(this.onPiggyButtonClickHandle, this);
    }

    private build(): void {
        this.viewBackground = new Sprite(this.commonResources.assets_common_blured_backgrounds_blurry_lobby_jpg);
        this.viewBackground.eventMode = "static";
        this.tokenBackground = new Sprite(this.introResources.assets_views_piggy_intro_tocken_background_png);
        this.topLight = new Sprite(this.introResources.assets_views_piggy_intro_blue_light_top_png);
        this.leftBlueLight = new Sprite(this.introResources.assets_views_piggy_intro_blue_light_vertical_png);
        this.rightBlueLight = new Sprite(this.introResources.assets_views_piggy_intro_blue_light_vertical_png);
        this.rightBlueLight.scale.x = -1;
        this.bottomLight = new Sprite(this.introResources.assets_views_piggy_intro_blue_light_bottom_png);
        this.bigPiggy = new Sprite(this.introResources.assets_views_piggy_intro_big_piggy_png);
        this.header = new CheckoutPiggyHeaderComponent(this.introResources);
        this.tokenDescription = new CheckoutPiggyTokenDescriptionComponent(this.commonResources, this.introResources);
        this.piggyDescription = new CheckoutPiggyPiggyDescriptionComponent(this.commonResources);
        this.claimDescription = new CheckoutPiggyClaimDescriptionComponent(this.commonResources, this.introResources);
        this.piggyButton = new ButtonWithScaleAnimation(this.introResources.assets_views_piggy_intro_piggy_png);
        this.closeButton = new ButtonWithScaleAnimation(this.commonResources.assets_common_ui_x_png);
        this.closeButton.hitArea = new Rectangle(-this.closeButton.width * 0.5, -this.closeButton.height, this.closeButton.width * 2, this.closeButton.height * 2);
        this.hand = new Hand(this.commonResources);
        this.hand.scale.set(0.5);
        this.hand.pivot.set(this.hand.width * 0.5, this.hand.height * 0.5);
        this.hand.angle = 132;
        this.inputBlocker = new FullscreenInputBlocker(this.canvasScaler);
        this.inputBlocker.blocked = false;

        this.addChild(
            this.viewBackground,
            this.topLight,
            this.leftBlueLight,
            this.rightBlueLight,
            this.bottomLight,
            this.tokenBackground,
            this.bigPiggy,
            this.header,
            this.tokenDescription,
            this.piggyDescription,
            this.claimDescription,
            this.piggyButton,
            this.closeButton,
            this.hand,
            this.inputBlocker
        );
    }

    private layout(width: number, _height: number): void {
        this.leftBlueLight.position.set(0, 221);
        this.rightBlueLight.position.set(width, 111);
        this.bottomLight.position.set(6, 1479);
        this.bigPiggy.position.set(0, 526);
        this.header.position.set(132, 150);
        this.tokenDescription.position.set(250, 737);
        this.piggyDescription.position.set(372, 1051);
        this.claimDescription.position.set(252, 1466);
        this.piggyButton.position.set(0, 171);
        this.closeButton.position.set(688, 127);
        this.hand.position.set(335, 529);
    }

    private async show(): Promise<void> {
        this.animations.fromTo(this.viewBackground, { alpha: 0 }, { alpha: 1, duration: 0.3, ease: "power1.in" });
        this.animations.fromTo(this.piggyButton.scale, { x: 0.6, y: 0.6 }, { x: this.piggyButton.scale.x, y: this.piggyButton.scale.y, duration: 0.5, ease: "power1.out" });
        await Promise.all([
            this.animations.play(),
            this.showHand()
        ]);
        this.animations.clear();
    }

    private async hide(): Promise<void> {
        this.animations.to(this, { alpha: 0, duration: 0.2, ease: "power1.out" });
        await this.animations.play();
        this.animations.clear();
    }

    private async showHand(): Promise<void> {
        this.animations.fromTo(this.hand.scale, { x: 0, y: 0 }, { x: this.hand.scale.x, y: this.hand.scale.y, duration: 0.3, ease: "power1.out" });
        await this.animations.play();
        this.animations.clear();
    }

    private async hideHand(): Promise<void> {
        this.animations.fromTo(this.hand.scale, { x: this.hand.scale.x, y: this.hand.scale.y }, { x: 0, y: 0, duration: 0.3, ease: "power1.out" });
        await this.animations.play();
        this.animations.clear();
    }

    private async onCloseButtonClickHandle(): Promise<void> {
        this.inputBlocker.blocked = true;
        await this.hideHand();
        await this.hide();
        this.inputBlocker.blocked = false;
        this.piggyAnimationPcs?.complete();
        this.operationControls.complete();
    }

    private async onPiggyButtonClickHandle(): Promise<void> {
        this.inputBlocker.blocked = true;
        this.soundResources.assets_sounds_Choose_Card_mp3.play();
        this.gameController.showPiggyPopup(CurrentPiggyState.NEW_PIGGY);
        await this.hideHand();
        await this.hide();
        this.inputBlocker.blocked = false;
        this.operationControls.complete();
    }

    public async start(): Promise<void> {
        await this.show();
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.closeButton.onUp.remove(this.onCloseButtonClickHandle, this);
        this.piggyButton.onUp.remove(this.onPiggyButtonClickHandle, this);
        super.destroy(options);
    }
}
